import { useCallback, useEffect, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { useMessageManager } from '@framework/message';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import CompanyRepository from '@/creditflowresearch/infrastructure/repository/CompanyRepository';
import {
  ITopCompaniesCorporateResponse,
  ITopCompaniesPayload,
  ICorporateTopFilterPayload,
} from '@/creditflowresearch/application/TopCompanies/corporateTopCompanies/interfaces';
import useCorporateTopCompanyFilters from './useCorporateTopCompanyFilters';
import {
  useTopCompanyCommonCorporateContext,
} from '@/creditflowresearch/application/TopCompanies/topCompaniesCommonCorporate/TopCompaniesCommonCorporateContext';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';

type IncludedRole = string;

interface IUseCorporateTopCompanyProps {
  serverData: ITopCompaniesCorporateResponse & { filters: ICorporateTopFilterPayload },
  companyRole: IncludedRole,
}

const useCorporateTopCompany = ({ serverData, companyRole }: IUseCorporateTopCompanyProps) => {
  const container = getContainer();
  const { getErrorMessage } = useMessageManager();
  const appliedFilters = useCorporateTopCompanyFilters({ serverFilters: serverData?.filters });
  const [data, setData] = useState<ITopCompaniesCorporateResponse>(serverData);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const { activeTab, selectedPeriod, setIsExportAvailable } = useTopCompanyCommonCorporateContext();

  const fetchData = useCallback(async ({ page }) => {
    setLoading(true);
    try {
      const companyRepository = container.get<CompanyRepository>(CompanyRepository);
      const payload: ITopCompaniesPayload = {
        filters: {
          ...appliedFilters,
          includedRole: companyRole,
          dateFrom: selectedPeriod?.dateFrom,
          dateTo: selectedPeriod?.dateTo,
        },
        paging: { page, perPage: 9 },
      };
      const response = await companyRepository.getCorporateTopCompanies(payload);
      setData(response);
      setIsExportAvailable(response.totalCount !== 0);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, companyRole, selectedPeriod, container, getErrorMessage]);

  const paginate = useCallback((pageNum: number) => setPage(pageNum), []);

  useEffect(() => {
    setPage(1);
  }, [selectedPeriod]);

  useAutoFetch(() => {
    if (activeTab !== companyRole) {
      return;
    }
    fetchData({ page });
  }, [activeTab, companyRole, appliedFilters, page, selectedPeriod]);

  return {
    data,
    loading,
    page,
    paginate,
  };
};

export default useCorporateTopCompany;
